import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Sparkles, Plus, Zap, BarChart3, Brain, Sliders, Bookmark, Layers, Rocket,
  Play, Pause, ChevronRight, ChevronLeft, X, RotateCcw,
} from 'lucide-react';
import { TUTORIAL_VIDEO_URL, TUTORIAL_SCENES, TUTORIAL_STORAGE } from '../../config/tutorial';
import { fireTutorialEvent, TUTORIAL_EVENTS } from '../../lib/tutorialAnalytics';

const ICONS = { Sparkles, Plus, Zap, BarChart3, Brain, Sliders, Bookmark, Layers, Rocket };

/**
 * TutorialModal — video player + slide-based fallback.
 *
 * Props:
 *   open     : boolean — whether the modal is visible
 *   onClose  : () => void — called on any close (X, esc, skip, completion)
 *   trigger  : 'launcher' | 'welcome' | 'replay' | 'help' — where the open
 *              action came from (drives analytics: started vs replayed).
 */
export default function TutorialModal({ open, onClose, trigger = 'launcher', onCtaAnalyze }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sceneIdx, setSceneIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1 within the current scene
  const startedRef = useRef(false);
  const rafRef = useRef(null);
  const sceneStartRef = useRef(0);
  const pausedAtRef = useRef(0);

  const hasVideo = Boolean(TUTORIAL_VIDEO_URL);
  const scenes = TUTORIAL_SCENES;
  const scene = scenes[sceneIdx];

  // Fire "started" / "replayed" exactly once per open.
  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      return;
    }
    if (!startedRef.current) {
      startedRef.current = true;
      const evt =
        trigger === 'replay' ? TUTORIAL_EVENTS.REPLAYED : TUTORIAL_EVENTS.STARTED;
      fireTutorialEvent(evt, { trigger });
      setSceneIdx(0);
      setPaused(false);
      setProgress(0);
      sceneStartRef.current = performance.now();
      pausedAtRef.current = 0;
    }
  }, [open, trigger]);

  // Scene progression loop (slideshow fallback only).
  useEffect(() => {
    if (!open || hasVideo || paused) return;
    sceneStartRef.current = performance.now() - progress * scene.duration;

    const tick = () => {
      const elapsed = performance.now() - sceneStartRef.current;
      const p = Math.min(1, elapsed / scene.duration);
      setProgress(p);
      if (p >= 1) {
        if (sceneIdx < scenes.length - 1) {
          setSceneIdx((i) => i + 1);
          setProgress(0);
        } else {
          markCompleted();
        }
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [open, hasVideo, paused, sceneIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const markCompleted = useCallback(() => {
    try {
      localStorage.setItem(TUTORIAL_STORAGE.COMPLETED, 'true');
    } catch { /* ignore */ }
    fireTutorialEvent(TUTORIAL_EVENTS.COMPLETED, { via: hasVideo ? 'video' : 'slides' });
    onClose?.();
  }, [hasVideo, onClose]);

  const markSkipped = useCallback(() => {
    fireTutorialEvent(TUTORIAL_EVENTS.SKIPPED, {
      scene_id: scene?.id,
      scene_index: sceneIdx,
    });
    onClose?.();
  }, [onClose, scene, sceneIdx]);

  const goNext = () => {
    if (sceneIdx < scenes.length - 1) {
      setSceneIdx((i) => i + 1);
      setProgress(0);
    } else {
      markCompleted();
    }
  };
  const goPrev = () => {
    if (sceneIdx > 0) {
      setSceneIdx((i) => i - 1);
      setProgress(0);
    }
  };

  const handleCtaAnalyze = () => {
    fireTutorialEvent(TUTORIAL_EVENTS.CTA_ANALYZE, {});
    try { localStorage.setItem(TUTORIAL_STORAGE.COMPLETED, 'true'); } catch { /* ignore */ }
    onClose?.();
    if (onCtaAnalyze) {
      onCtaAnalyze();
    } else {
      navigate('/analyze');
    }
  };

  const totalDuration = useMemo(
    () => scenes.reduce((sum, s) => sum + s.duration, 0),
    [scenes]
  );

  const globalProgress = useMemo(() => {
    // On the final scene, always show 100% so the bar visually completes.
    if (sceneIdx >= scenes.length - 1) return 1;
    const before = scenes
      .slice(0, sceneIdx)
      .reduce((s, x) => s + x.duration, 0);
    return (before + progress * scene.duration) / totalDuration;
  }, [scenes, sceneIdx, progress, scene, totalDuration]);

  const Icon = ICONS[scene?.icon] || Sparkles;
  const bullets = scene?.bulletsKey
    ? t(scene.bulletsKey, { returnObjects: true, defaultValue: [] })
    : [];

  const handleOpenChange = (v) => {
    if (!v) markSkipped();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="tutorial-modal"
        className="max-w-3xl w-[95vw] p-0 overflow-hidden bg-white dark:bg-[#0B0E14] border-slate-200 dark:border-slate-800 rounded-2xl"
      >
        <DialogTitle className="sr-only">
          {t('tutorial.modalTitle', 'RendPleX Tutorial')}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {t('tutorial.modalDescription', 'A short walkthrough of how to analyze a property with RendPleX.')}
        </DialogDescription>

        {/* Skip / close bar */}
        <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
          <button
            data-testid="tutorial-skip-button"
            onClick={markSkipped}
            className="text-xs font-medium text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-full px-3 py-1 transition"
          >
            {t('tutorial.skip')}
          </button>
          <button
            data-testid="tutorial-close-button"
            onClick={markSkipped}
            aria-label={t('tutorial.close')}
            className="text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-1.5 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Video mode ---------------------------------------------------- */}
        {hasVideo ? (
          <div className="bg-black">
            <video
              data-testid="tutorial-video"
              src={TUTORIAL_VIDEO_URL}
              controls
              autoPlay
              className="w-full aspect-video"
              onEnded={markCompleted}
            />
            <div className="p-5 flex items-center justify-between gap-3 bg-white dark:bg-[#0B0E14] border-t border-slate-100 dark:border-slate-800">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t('tutorial.videoHint')}
              </p>
              <Button
                data-testid="tutorial-cta-analyze"
                onClick={handleCtaAnalyze}
                className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl font-semibold"
              >
                {t('tutorial.ctaAnalyze')}
              </Button>
            </div>
          </div>
        ) : (
          /* Slideshow mode ---------------------------------------------- */
          <div className="flex flex-col">
            {/* Scene stage */}
            <div
              data-testid={`tutorial-scene-${scene.id}`}
              className={`relative w-full sm:aspect-video bg-gradient-to-br ${scene.accent} overflow-hidden`}
            >
              {/* Grain overlay for depth */}
              <div className="absolute inset-0 opacity-[0.15] mix-blend-overlay pointer-events-none"
                   style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '3px 3px' }} />
              {/* Content */}
              <div className="relative h-full w-full flex flex-col items-start justify-end p-6 pt-14 sm:p-10 text-white">
                <div className="mb-3 sm:mb-4 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/25">
                  <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <h2
                  data-testid="tutorial-scene-title"
                  className="font-primary font-extrabold text-2xl sm:text-4xl tracking-tight leading-tight max-w-2xl"
                >
                  {t(scene.titleKey)}
                </h2>
                <p className="mt-1.5 sm:mt-2 text-sm sm:text-lg text-white/85 max-w-2xl">
                  {t(scene.subtitleKey)}
                </p>
                {Array.isArray(bullets) && bullets.length > 0 && (
                  <ul className="mt-3 sm:mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs sm:text-sm text-white/85 max-w-2xl">
                    {bullets.map((b, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-white/70 flex-shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-4 sm:mt-5 text-[10px] sm:text-xs uppercase tracking-widest text-white/60">
                  {t('tutorial.sampleDataLabel')}
                </p>
              </div>
              {/* Scene counter */}
              <div className="absolute top-3 left-3 sm:top-4 sm:left-4 text-[10px] sm:text-[11px] font-semibold uppercase tracking-widest text-white/80 bg-black/25 rounded-full px-2.5 py-1 sm:px-3">
                {sceneIdx + 1} / {scenes.length}
              </div>
            </div>

            {/* Narration */}
            <div className="px-6 sm:px-8 py-5 bg-white dark:bg-[#0B0E14]">
              <p
                data-testid="tutorial-narration"
                className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed"
              >
                <span className="text-[#45C04A] mr-2">▸</span>
                {t(scene.narrationKey)}
              </p>
            </div>

            {/* Progress bar */}
            <div className="h-1 bg-slate-100 dark:bg-slate-800">
              <div
                data-testid="tutorial-progress"
                className="h-full bg-[#45C04A] transition-[width] duration-200 ease-linear"
                style={{ width: `${Math.round(globalProgress * 100)}%` }}
              />
            </div>

            {/* Controls */}
            <div className="px-4 sm:px-6 py-4 flex items-center justify-between gap-2 bg-slate-50 dark:bg-[#131824] border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <Button
                  data-testid="tutorial-prev"
                  onClick={goPrev}
                  disabled={sceneIdx === 0}
                  variant="ghost"
                  size="sm"
                  className="text-slate-600 dark:text-slate-300"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {t('tutorial.previous')}
                </Button>
                <Button
                  data-testid="tutorial-play-pause"
                  onClick={() => setPaused((p) => !p)}
                  variant="ghost"
                  size="sm"
                  className="text-slate-600 dark:text-slate-300"
                >
                  {paused ? <Play className="w-4 h-4 mr-1" /> : <Pause className="w-4 h-4 mr-1" />}
                  {paused ? t('tutorial.play') : t('tutorial.pause')}
                </Button>
              </div>

              <div className="flex items-center gap-2">
                {scene.isEnding ? (
                  <>
                    <Button
                      data-testid="tutorial-cta-explore"
                      onClick={markCompleted}
                      variant="outline"
                      className="rounded-xl border-slate-200 dark:border-slate-700"
                    >
                      {t('tutorial.ctaExplore')}
                    </Button>
                    <Button
                      data-testid="tutorial-cta-analyze"
                      onClick={handleCtaAnalyze}
                      className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl font-semibold"
                    >
                      {t('tutorial.ctaAnalyze')}
                    </Button>
                  </>
                ) : (
                  <Button
                    data-testid="tutorial-next"
                    onClick={goNext}
                    className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl font-semibold"
                  >
                    {t('tutorial.next')}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>

            {/* Disclaimer */}
            <div className="px-6 py-3 text-[11px] text-slate-400 dark:text-slate-500 bg-white dark:bg-[#0B0E14] border-t border-slate-100 dark:border-slate-800 flex items-center gap-2">
              <RotateCcw className="w-3 h-3" />
              {t('tutorial.disclaimer')}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
